import { Text as FluentText } from '@fluentui/react-components';
import cn from 'classnames';
import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import './Text.scss';

/** Allowed HTML elements */
export type TextElement =
  | 'span'
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'strong'
  | 'em'
  | 'b'
  | 'i'
  | 'pre';

/** State-based text variants */
export type TextVariant = 'default' | 'disabled' | 'timestamp';

/** Semantic text colors */
export type TextColor =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'success'
  | 'warning'
  | 'muted';

/** Text size options */
export type TextSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl';

/** Font weight options */
export type TextWeight = 'normal' | 'medium' | 'semibold' | 'bold';

// Fluent UI internal types (not exported by @fluentui/react-components)
type FluentTextSize = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 1000;
type FluentTextWeight = 'regular' | 'medium' | 'semibold' | 'bold';

// Internal mapping: our sizes to Fluent sizes
const sizeToFluentSize: Record<TextSize, FluentTextSize> = {
  xs: 100,
  sm: 200,
  md: 300,
  lg: 400,
  xl: 500,
  '2xl': 600,
  '3xl': 700,
};

// Internal mapping: our weights to Fluent weights
const weightToFluentWeight: Record<TextWeight, FluentTextWeight> = {
  normal: 'regular',
  medium: 'medium',
  semibold: 'semibold',
  bold: 'bold',
};

export interface TextProps extends Omit<HTMLAttributes<HTMLElement>, 'color'> {
  /** Text content */
  children: ReactNode;
  /** HTML element to render as */
  as?: TextElement;
  /** Semantic variant for text state */
  variant?: TextVariant;
  /** Semantic text color */
  color?: TextColor;
  /** Text size */
  size?: TextSize;
  /** Font weight */
  weight?: TextWeight;
  /** Truncate text with ellipsis */
  truncate?: boolean;
  /** Make text italic */
  italic?: boolean;
}

/**
 * Text component with semantic API.
 * Implementation uses Fluent UI internally but exposes a library-agnostic interface.
 *
 * @deprecated Do not use in new code. Render the semantic element directly and
 * style it with the global.css text utility classes (`font-semibold`, `font-bold`,
 * `truncate`, `text-center`, `text-grey`, `text-error`, …) from
 * `shared-frontend/components/src/styles/_utils.scss`, e.g.
 * `<span className="font-semibold truncate">`. When a typography ramp size or
 * weight is genuinely needed, use Fluent UI's own `Text` from
 * `@fluentui/react-components`. Existing usages remain functional; migration of
 * those is tracked separately.
 */
export const Text = forwardRef<HTMLElement, TextProps>(
  (
    {
      children,
      as: element = 'span',
      variant = 'default',
      color,
      size = 'md',
      weight = 'normal',
      truncate = false,
      italic = false,
      className,
      style,
      ...rest
    },
    ref,
  ) => {
    const textClassName = cn(
      'aix-text',
      {
        [`aix-text--${variant}`]: variant !== 'default',
        [`aix-text--color-${color}`]: color,
        'aix-text--italic': italic,
      },
      className,
    );

    // Map to Fluent UI internals (implementation detail)
    const fluentSize = sizeToFluentSize[size];
    const fluentWeight = weightToFluentWeight[weight];

    return (
      <FluentText
        // Ref cast needed: we accept HTMLElement for flexibility, Fluent expects HTMLSpanElement
        ref={ref as React.Ref<HTMLSpanElement>}
        as={element}
        className={textClassName}
        style={style}
        truncate={truncate}
        weight={fluentWeight}
        size={fluentSize}
        aria-disabled={variant === 'disabled' || undefined}
        {...rest}
      >
        {children}
      </FluentText>
    );
  },
);

Text.displayName = 'Text';
